// ChannelAttachModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { tokenizeSearchQuery } from 'services/search/SearchIndexService';
import { searchBaseNodes } from 'services/search/BaseNodeSearchService';

// CSS
import './ChannelAttachModal.css';

// Components
import FindExistingSection from 'components/addmodal/sections/find/FindExistingSection';
import Button from 'components/button/Button';
import Divider from 'components/divider/Divider';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';

/**
 * ChannelAttachModal
 *
 * Search and multi-select existing base nodes to attach.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Close callback.
 * @param {string} baseKey - Active base key.
 * @param {function} onAttach - Called with the selected node rows when confirming.
 * @param {string[]} [excludeNodeKeys] - Node keys already attached (hidden from results).
 *
 * @returns {ReactNode} The rendered modal.
 */
const ChannelAttachModal = React.memo(({
  isOpen,
  onClose,
  baseKey,
  onAttach,
  excludeNodeKeys = [],
}) => {

  // ----- CONTEXT ----- //

  const { getAll } = useNodeTypes();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedByKey, setSelectedByKey] = useState(() => new Map());

  // ----- MEMOS ----- //

  const availableTypes = useMemo(() => {
    const rows = Array.isArray(getAll?.()) ? getAll() : [];
    return [...rows].sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
  }, [getAll]);

  const availableTypeMap = useMemo(() => {
    const map = new Map();
    availableTypes.forEach((typeDef) => {
      if (!typeDef?.key) return;
      map.set(typeDef.key, typeDef);
    });
    return map;
  }, [availableTypes]);

  const excludedKeySet = useMemo(() => {
    const keys = new Set();
    (Array.isArray(excludeNodeKeys) ? excludeNodeKeys : []).forEach((key) => {
      const clean = String(key || '').trim();
      if (clean) keys.add(clean);
    });
    return keys;
  }, [excludeNodeKeys]);

  const normalizedSearchTokens = useMemo(() => {
    return tokenizeSearchQuery(searchTerm);
  }, [searchTerm]);

  const queryToken = normalizedSearchTokens.length
    ? normalizedSearchTokens[normalizedSearchTokens.length - 1]
    : '';

  const visibleSearchResults = useMemo(() => {
    return (Array.isArray(searchResults) ? searchResults : []).filter((row) => {
      const key = String(row?.key || '').trim();
      return key && !excludedKeySet.has(key);
    });
  }, [excludedKeySet, searchResults]);

  const selectedRows = useMemo(() => Array.from(selectedByKey.values()), [selectedByKey]);
  const selectedNodeKeys = useMemo(() => Array.from(selectedByKey.keys()), [selectedByKey]);
  const selectedCount = selectedRows.length;

  const hasSearchQuery = String(searchTerm || '').trim().length > 0;
  const noResults = Boolean(queryToken)
    && !searching
    && !visibleSearchResults.length
    && !searchError;

  const attachLabel = selectedCount > 1
    ? `Attach ${selectedCount}`
    : 'Attach';

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) return;

    setSearchTerm('');
    setSearchResults([]);
    setSearching(false);
    setSearchError('');
    setSelectedByKey(new Map());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    if (!queryToken || !baseKey) {
      setSearchResults([]);
      setSearching(false);
      setSearchError('');
      return undefined;
    }

    let cancelled = false;

    setSearching(true);
    setSearchError('');

    const handle = setTimeout(async () => {
      try {
        const rows = await searchBaseNodes({
          rawQuery: searchTerm,
          baseKey,
          max: 50,
        });

        if (cancelled) return;

        const filteredRows = (rows || []).filter((row) => row?.key);
        filteredRows.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || '')));
        setSearchResults(filteredRows);
      } catch (error) {
        if (cancelled) return;
        setSearchResults([]);
        setSearchError(error?.message || 'Failed to search items.');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isOpen, queryToken, searchTerm, baseKey]);

  // ----- HANDLERS ----- //

  /**
   * Toggles a search result in the multi-select set.
   *
   * @param {object} row - Candidate node row.
   */
  const handleToggleExisting = useCallback((row) => {
    const nodeKey = String(row?.key || '').trim();
    if (!nodeKey) return;

    setSelectedByKey((prev) => {
      const next = new Map(prev);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.set(nodeKey, row);
      }
      return next;
    });
  }, []);

  /**
   * Confirms the current selection and closes the modal.
   */
  const handleConfirmAttach = useCallback(() => {
    if (!selectedRows.length) return;
    onAttach?.(selectedRows);
    onClose?.();
  }, [onAttach, onClose, selectedRows]);

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="add"
      title="Attach"
      isOpen={isOpen}
      width={isMobile ? '100%' : 520}
      height="80%"
      onClose={onClose}
    >
      <div className="channel-attach-modal-container">

        {/* SEARCH CONTROLS */}
        <div className="channel-attach-modal-search-controls">
          <TextInput
            value={searchTerm}
            placeholder="Search existing items..."
            autoFocus={!isMobile}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <Divider margin={6} />

        {/* CONTENT */}
        <div className="channel-attach-modal-content">
          {hasSearchQuery ? (
            <FindExistingSection
              hideInput
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchError={searchError}
              searching={searching}
              noResults={noResults}
              searchResults={visibleSearchResults}
              availableTypeMap={availableTypeMap}
              anchorNodeKey=""
              attachingNodeKey=""
              enableSelectionWithoutAnchor
              selectedNodeKeys={selectedNodeKeys}
              onAttachExisting={handleToggleExisting}
            />
          ) : (
            <div className="channel-attach-modal-empty">
              Search for items in this base to attach.
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="channel-attach-modal-actions">
          <div className="channel-attach-modal-selection-count">
            {selectedCount > 0
              ? `${selectedCount} selected`
              : 'Select one or more items'}
          </div>
          <Button
            label={attachLabel}
            enabled={selectedCount > 0}
            onClick={handleConfirmAttach}
          />
        </div>
      </div>
    </Modal>
  );
});

// Dev Tools
ChannelAttachModal.displayName = 'ChannelAttachModal';

export default ChannelAttachModal;
